import { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { ApiRequest } from "GlobalFunctions";
import AlertDialog from "elements/AlertDialog";

export default function FaqCategories(props: any) {
  const { open, setOpen, setLoading, loadCategories } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [list, setList] = useState<any[]>([]);
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    if (open) { loadList(); resetForm(); }
  }, [open]);

  const loadList = () => {
    ApiRequest({
      url: "/faq/categories/list",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      query: { full_list: true },
      setResponse: (response: any) => {
        setList(response.list || []);
      },
    });
  };

  const handleSave = () => {
    if (!name.trim()) { enqueueSnackbar("Ingrese un nombre", { variant: "warning" }); return; }
    setLoading(true);
    const body = new FormData();
    body.append("formValues", JSON.stringify({
      faq_category_id: editId || "null",
      name: name.trim(),
      sort_order: sortOrder,
    }));

    ApiRequest({
      url: "/faq/categories/addedit",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body,
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar(editId ? "Categoria actualizada" : "Categoria creada", { variant: "success" });
          resetForm();
          loadList();
          loadCategories();
        }
        setLoading(false);
      },
    });
  };

  const handleDelete = () => {
    if (!deleteId) return;
    const body = new FormData();
    body.append("faq_category_id", String(deleteId));

    ApiRequest({
      url: "/faq/categories/delete",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body,
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar("Categoria eliminada", { variant: "success" });
          loadList();
          loadCategories();
        }
      },
    });
  };

  const handleEdit = (cat: any) => {
    setEditId(cat.faq_category_id);
    setName(cat.name);
    setSortOrder(cat.sort_order);
    setIsAdding(false);
  };

  const resetForm = () => {
    setEditId(null);
    setName("");
    setSortOrder(0);
    setIsAdding(false);
  };

  const startAdding = () => {
    resetForm();
    setIsAdding(true);
  };

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <div>
            <Typography variant="h6" component="span">Categorias</Typography>
            <Typography variant="body2" color="text.secondary" display="block">
              Organiza las preguntas por temas
            </Typography>
          </div>
          <Button variant="contained" size="small" onClick={startAdding} disabled={isAdding}>
            Agregar
          </Button>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {/* Inline form for add/edit */}
          {(isAdding || editId) && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "16px 24px", backgroundColor: "#f5f7fa" }}>
              <TextField
                size="small"
                placeholder="Nombre de categoria"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
                sx={{ flex: 1 }}
                autoFocus
              />
              <TextField
                size="small"
                placeholder="Orden"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
                sx={{ width: 80 }}
              />
              <Tooltip title="Guardar">
                <IconButton color="primary" onClick={handleSave} size="small">
                  <CheckIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancelar">
                <IconButton onClick={resetForm} size="small">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}

          {/* Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#a8a8a8", width: 60 }}>Orden</TableCell>
                  <TableCell sx={{ color: "#a8a8a8" }}>Nombre</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", width: 80 }} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((cat) => (
                  <TableRow
                    key={cat.faq_category_id}
                    hover
                    selected={editId === cat.faq_category_id}
                  >
                    <TableCell>{cat.sort_order}</TableCell>
                    <TableCell>{cat.name}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEdit(cat)}>
                          <ModeEditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => { setDeleteId(cat.faq_category_id); setOpenAlert(true); }}>
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {list.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, color: "#999" }}>
                      No hay categorias. Crea la primera.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} variant="outlined">Cerrar</Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={openAlert}
        setOpen={setOpenAlert}
        subtitle="Se eliminara esta categoria y todas sus preguntas asociadas."
        send={handleDelete}
      />
    </>
  );
}
